import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import LiteShell from './LiteShell';
import { liteApi, litePro } from '@/utils/lite/liteApi';

/**
 * LiteBillingSuccess — landing page after Paystack redirects back to us
 * for Lite Pro purchases. Verifies the reference, stores the unlock token
 * in localStorage, and shows a confirmation screen.
 */
export default function LiteBillingSuccess() {
  const [params] = useSearchParams();
  const reference = params.get('reference') || '';
  const [state, setState] = useState({ status: 'verifying', error: '', billing_period: 'monthly', expires_at: '' });

  useEffect(() => {
    let cancelled = false;
    if (!reference) {
      setState({ status: 'error', error: 'Missing payment reference. If you completed payment, please contact support.' });
      return;
    }
    (async () => {
      try {
        const res = await liteApi.verifyLitePro(reference);
        if (cancelled) return;
        // Persist unlock token + email (email was the one used at checkout — Paystack returned it via metadata)
        const email = res.email || (() => {
          try { return localStorage.getItem(litePro.EMAIL_KEY) || ''; } catch { return ''; }
        })();
        litePro.setActive(email, res.token, res.expires_at);
        setState({ status: 'success', error: '', billing_period: res.billing_period, expires_at: res.expires_at });
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', error: e.message || 'Could not verify your payment.' });
      }
    })();
    return () => { cancelled = true; };
  }, [reference]);

  return (
    <LiteShell currentStep={1} totalSteps={1}>
      <div className="max-w-xl mx-auto pt-8" data-testid="lite-billing-success">
        {state.status === 'verifying' && (
          <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin mb-3" />
            <h1 className="text-2xl font-serif text-primary-900 mb-2">Verifying your payment…</h1>
            <p className="text-stone-500 text-sm">Hang tight — this only takes a few seconds.</p>
          </div>
        )}

        {state.status === 'success' && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-8 text-center" data-testid="lite-pro-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
            <h1 className="text-2xl font-serif text-primary-900 mb-2">You're now Lite Pro!</h1>
            <p className="text-stone-700 leading-relaxed mb-4">
              Unlimited AI generations are unlocked on this device.{' '}
              {state.billing_period === 'annual' ? 'Your annual plan' : 'Your monthly plan'} renews on{' '}
              <strong>{state.expires_at ? new Date(state.expires_at).toLocaleDateString() : '—'}</strong>.
            </p>
            <p className="text-xs text-stone-500 mb-6">
              💡 Using a different browser or device? Just enter the same email and we'll send a magic link to unlock there too.
            </p>
            <Link
              to="/us/tools"
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary text-white font-medium hover:bg-primary/90"
              data-testid="lite-pro-go-tools"
            >
              Open Lite tools →
            </Link>
          </div>
        )}

        {state.status === 'error' && (
          <div className="rounded-2xl border border-red-200 bg-red-50/40 p-8 text-center" data-testid="lite-pro-error">
            <AlertCircle className="w-10 h-10 mx-auto text-red-600 mb-3" />
            <h1 className="text-2xl font-serif text-primary-900 mb-2">Something didn't go through</h1>
            <p className="text-stone-700 mb-4">{state.error}</p>
            <Link
              to="/us"
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-stone-100 text-stone-800 font-medium hover:bg-stone-200"
            >
              Back to Lite
            </Link>
          </div>
        )}
      </div>
    </LiteShell>
  );
}
